import { z } from "zod";
import { offsetDateTimeWithOptionalSecondsSchema } from "./primitives.js";
export const GRANOLA_LIST_MEETINGS_PARAMETERS = {
    type: "object",
    additionalProperties: false,
    properties: {
        updated_after: {
            type: "string",
            format: "date-time",
            description: "Optional inclusive ISO 8601 lower bound for recently updated meetings.",
        },
        page_size: {
            type: "integer",
            minimum: 1,
            maximum: 25,
            description: "Maximum meetings to return (default 10).",
        },
        cursor: {
            type: "string",
            maxLength: 2048,
            description: "Opaque cursor returned by an earlier Granola list call.",
        },
    },
};
export const GRANOLA_GET_MEETINGS_PARAMETERS = {
    type: "object",
    additionalProperties: false,
    properties: {
        meeting_ids: {
            type: "array",
            minItems: 1,
            maxItems: 5,
            items: { type: "string", minLength: 1, maxLength: 512 },
            description: "Exact Granola meeting ids returned by granola_list_meetings.",
        },
        include_transcript: {
            type: "boolean",
            description: "Include bounded transcript entries. Defaults to false.",
        },
    },
    required: ["meeting_ids"],
};
const listOperationSchema = z.object({
    type: z.literal("list"),
    // Agent-written bound; see offsetDateTimeWithOptionalSecondsSchema.
    updatedAfter: offsetDateTimeWithOptionalSecondsSchema.optional(),
    pageSize: z.number().int().min(1).max(25).default(10),
    cursor: z.string().trim().min(1).max(2048).optional(),
}).strict();
const getOperationSchema = z.object({
    type: z.literal("get"),
    meetingIds: z.array(z.string().trim().min(1).max(512)).min(1).max(5),
    includeTranscript: z.boolean().default(false),
}).strict();
export const granolaDirectRequestSchema = z.object({
    runId: z.string().trim().min(1).max(256),
    tenantSlug: z.string().trim().min(1).max(128),
    operation: z.discriminatedUnion("type", [listOperationSchema, getOperationSchema]),
}).strict();
const personSchema = z.object({
    name: z.string().nullable(),
    email: z.string().nullable(),
});
const meetingSummarySchema = z.object({
    id: z.string(),
    title: z.string().nullable(),
    owner: personSchema.nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
export const granolaDirectResponseSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("list"),
        meetings: z.array(meetingSummarySchema),
        hasMore: z.boolean(),
        cursor: z.string().nullable(),
    }),
    z.object({
        type: z.literal("get"),
        meetings: z.array(meetingSummarySchema.extend({
            webUrl: z.string().nullable(),
            summaryText: z.string().nullable(),
            summaryMarkdown: z.string().nullable(),
            attendees: z.array(personSchema),
            calendarEvent: z.record(z.string(), z.unknown()).nullable(),
            transcript: z.array(z.object({
                speaker: z.string(),
                text: z.string(),
                startTime: z.string().nullable(),
                endTime: z.string().nullable(),
            })),
            transcriptTruncated: z.boolean(),
        })),
    }),
]);
